import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { Breadcrumb, Col, Divider, Row, Space, Spin } from 'antd'
import { isEmpty, size } from 'lodash'

import { UpdateDataText } from '../../../core/components/sections'

import { HistoryLink } from '../../../common/components/sections'
import { StyledPageSubTitle } from '../../../common/components/styled'
import {
  APP_ROUTES,
  EVENT_METADATA_TYPES,
  EVENT_TYPES,
  METADATA_TYPES,
} from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import {
  useAllergensQuery,
  useDocumentTitle,
  useIngredientsQuery,
  useUpdatedIngredientSubscription,
} from '../../../common/hooks'

import { renderAllergenEvent } from '../../helpers'
import { useAllergenQuery } from '../../hooks'
import {
  AllergenImageDetailsForm,
  AllergenIncludedAllergensDetailsForm,
  AllergenIncludedIngredientsDetailsForm,
  AllergenSimpleDetailsForm,
} from '../forms'

const AllergenDetailsPage = () => {
  const { id } = useParams()

  const isAllergenPageDirty = useSelector(
    store =>
      store.allergens.allergen.isIncludedIngredientsDirty ||
      store.allergens.allergen.isIncludedAllergensDirty ||
      store.allergens.allergen.isSimpleDetailsDirty,
  )

  const {
    isLoading: isFetchLoading,
    error: fetchError,
    data,
    refetch,
  } = useAllergenQuery(id)

  useUpdatedIngredientSubscription(
    id,
    isAllergenPageDirty,
    METADATA_TYPES.ALLERGENS.value,
    refetch,
  )

  const {
    data: ingredients,
    isLoading: isLoadingIngredients,
    error: ingredientsError,
  } = useIngredientsQuery()

  const {
    data: allergens,
    options: allergenOptions,
    isLoading: isLoadingAllergens,
    error: allergensError,
  } = useAllergensQuery()

  useDocumentTitle(`${data?.name || 'Loading Allergen...'}`)

  useEffect(() => {
    if (fetchError || ingredientsError || allergensError) {
      showErrorNotification({
        title: 'Allergen error',
        description: fetchError || ingredientsError || allergensError,
      })
    }
  }, [allergensError, fetchError, ingredientsError])

  return isFetchLoading ||
    isLoadingIngredients ||
    isLoadingAllergens ||
    isEmpty(data) ? (
    <Spin />
  ) : (
    <>
      <StyledPageSubTitle>
        <Row justify="space-between">
          <Col span={12}>
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to={APP_ROUTES.ALLERGENS.link}>
                  {APP_ROUTES.ALLERGENS.name}
                </Link>
              </Breadcrumb.Item>

              <Breadcrumb.Item>
                <Link to={`${APP_ROUTES.ALLERGENS.link}/${data.id}`}>
                  {data.name}
                </Link>
              </Breadcrumb.Item>
            </Breadcrumb>
          </Col>

          <Space>
            <UpdateDataText
              updatedAt={data.updatedAt}
              updatedBy={data.updatedBy?.name}
            />

            <HistoryLink
              renderEvent={renderAllergenEvent}
              entityId={id}
              eventTypes={[EVENT_TYPES.UPDATE]}
              eventMetadataTypes={[EVENT_METADATA_TYPES.ALLERGEN]}
            />
          </Space>
        </Row>
      </StyledPageSubTitle>
      <AllergenSimpleDetailsForm
        allergen={data}
        maxPosition={size(allergenOptions)}
      />
      <Divider />
      <AllergenImageDetailsForm allergen={data} />
      <Divider />
      {!isEmpty(allergenOptions) && (
        <AllergenIncludedAllergensDetailsForm
          allergenId={data.id}
          includedAllergens={data.includedAllergens}
          allergenOptions={allergenOptions}
        />
      )}
      <Divider />
      {!isEmpty(ingredients) && !isEmpty(allergens) && (
        <AllergenIncludedIngredientsDetailsForm
          allergenId={data.id}
          allergens={allergens}
          includedAllergens={data.includedAllergens}
          includedIngredients={data.includedIngredients}
          ingredientTreeData={ingredients}
        />
      )}
    </>
  )
}

export default AllergenDetailsPage
